import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, CalendarPlus, CheckCircle2, Headset, Percent } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { adminApi } from '@/api/admin'

const REASONS: { code: string; label: string }[] = [
  { code: 'too-expensive', label: "It's too expensive" },
  { code: 'missing-features', label: "It's missing features I need" },
  { code: 'not-using-enough', label: "I'm not using it enough" },
  { code: 'switching', label: "I'm switching to another system" },
  { code: 'too-complicated', label: "It's too complicated or hard to use" },
  { code: 'closing-or-pausing', label: 'My business is closing or pausing' },
  { code: 'just-trialling', label: 'I was just trialling it' },
  { code: 'other', label: 'Something else' },
]

function fmtDate(iso: string | null | undefined) {
  if (!iso) return null
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
}

type Done =
  | { kind: 'cancelled'; accessEndsAt: string | null }
  | { kind: 'trial'; trialEndsAt: string }
  | { kind: 'discount' }

function apiMessage(err: unknown, fallback: string) {
  const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
  return detail ?? fallback
}

export default function CancelSubscriptionPage() {
  const queryClient = useQueryClient()
  const [step, setStep] = useState<'reason' | 'offers'>('reason')
  const [reason, setReason] = useState<string | null>(null)
  const [detail, setDetail] = useState('')
  const [done, setDone] = useState<Done | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { data: options, isLoading } = useQuery({
    queryKey: ['cancellation-options'],
    queryFn: () => adminApi.getCancellationOptions(),
  })

  const body = { reason, reasonDetail: detail.trim() || null }
  const finish = (d: Done) => {
    setError(null)
    setDone(d)
    queryClient.invalidateQueries({ queryKey: ['billing-info'] })
    queryClient.invalidateQueries({ queryKey: ['cancellation-options'] })
  }

  const extendMutation = useMutation({
    mutationFn: () => adminApi.extendTrial(body),
    onSuccess: r => finish({ kind: 'trial', trialEndsAt: r.trialEndsAt }),
    onError: e => setError(apiMessage(e, 'Sorry, we could not extend your trial. Please try again.')),
  })
  const discountMutation = useMutation({
    mutationFn: () => adminApi.applyRetentionDiscount(body),
    onSuccess: () => finish({ kind: 'discount' }),
    onError: e => setError(apiMessage(e, 'Sorry, we could not apply the discount. Please try again.')),
  })
  const cancelMutation = useMutation({
    mutationFn: () => adminApi.cancelSubscription(body),
    onSuccess: r => finish({ kind: 'cancelled', accessEndsAt: r.accessEndsAt }),
    onError: e => setError(apiMessage(e, 'Sorry, we could not cancel your subscription. Please try again or contact support.')),
  })
  const busy = extendMutation.isPending || discountMutation.isPending || cancelMutation.isPending

  // ── Call or demo request (uses the normal support queue, so it reaches the team and the SuperAdmin inbox) ──
  const [callOpen, setCallOpen] = useState(false)
  const [phone, setPhone] = useState('')
  const [times, setTimes] = useState('')
  const [callSent, setCallSent] = useState(false)
  const callMutation = useMutation({
    mutationFn: () => adminApi.createSupportRequest({
      subject: 'Call or demo request (from the cancel screen)',
      message: [
        'Asked for a call or demo before cancelling.',
        phone.trim() && `Phone: ${phone.trim()}`,
        times.trim() && `Best times: ${times.trim()}`,
        reason && `Reason given: ${REASONS.find(r => r.code === reason)?.label}`,
        detail.trim() && `In their words: ${detail.trim()}`,
      ].filter(Boolean).join('\n'),
      category: 'CallRequest',
      pageUrl: '/admin/account/cancel',
    }),
    onSuccess: () => { setCallSent(true); setCallOpen(false) },
  })

  const backToBilling = (
    <Link to="/admin/account?tab=billing" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
      <ArrowLeft size={14} /> Back to billing
    </Link>
  )

  if (isLoading) {
    return <div className="flex justify-center py-16"><div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
  }

  // ── Finished ──
  if (done) {
    return (
      <div className="max-w-xl space-y-5">
        <div className="rounded-xl border border-gray-200 bg-white p-6 space-y-3">
          <div className="flex items-center gap-2 text-green-700 font-semibold">
            <CheckCircle2 size={20} />
            {done.kind === 'cancelled' && 'Your subscription is set to cancel'}
            {done.kind === 'trial' && 'Your trial has been extended'}
            {done.kind === 'discount' && 'Your discount has been applied'}
          </div>
          {done.kind === 'cancelled' && (
            <p className="text-sm text-slate-600">
              {options?.isAnnual
                ? `You've paid for the year, so you'll keep full access${done.accessEndsAt ? ` until ${fmtDate(done.accessEndsAt)}` : ' until the end of your paid year'}. We can't refund the remaining months, and you won't be charged again.`
                : `You'll keep full access${done.accessEndsAt ? ` until ${fmtDate(done.accessEndsAt)}` : ' until the end of your current period'}, and you won't be charged again.`}
              Changed your mind? You can keep your subscription from the Billing tab any time before then.
            </p>
          )}
          {done.kind === 'trial' && (
            <p className="text-sm text-slate-600">Your free trial now runs until {fmtDate(done.trialEndsAt)}. Nothing is charged before then.</p>
          )}
          {done.kind === 'discount' && (
            <p className="text-sm text-slate-600">You'll pay 50% less on your next two monthly payments, including any extra trainer seats. After that your normal price continues.</p>
          )}
        </div>
        {backToBilling}
      </div>
    )
  }

  if (!options?.canCancel) {
    return (
      <div className="max-w-xl space-y-4">
        <h1 className="text-2xl font-bold text-gray-900">Cancel subscription</h1>
        <p className="text-sm text-slate-600">
          There's no active subscription here that can be cancelled. If you think that's wrong, please contact support.
        </p>
        {backToBilling}
      </div>
    )
  }

  const accessEnds = fmtDate(options.accessEndsAt)

  return (
    <div className="max-w-xl space-y-6">
      <div className="space-y-2">
        {backToBilling}
        <h1 className="text-2xl font-bold text-gray-900">Cancel subscription</h1>
      </div>

      {step === 'reason' && (
        <div className="rounded-xl border border-gray-200 bg-white p-6 space-y-5">
          <div>
            <p className="font-semibold text-gray-900">We're sorry to see you thinking of leaving.</p>
            <p className="text-sm text-slate-500 mt-1">What's the main reason? This is optional, but it really helps us improve.</p>
          </div>

          <div className="space-y-2" role="radiogroup" aria-label="Reason for cancelling">
            {REASONS.map(r => (
              <label
                key={r.code}
                className={`flex items-center gap-3 rounded-lg border px-4 py-2.5 text-sm cursor-pointer transition-colors ${
                  reason === r.code ? 'border-indigo-500 bg-indigo-50 text-indigo-900' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input type="radio" name="reason" checked={reason === r.code} onChange={() => setReason(r.code)} className="accent-indigo-600" />
                {r.label}
              </label>
            ))}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="detail">Anything else you'd like to tell us? <span className="font-normal text-gray-400">(optional)</span></Label>
            <textarea
              id="detail"
              value={detail}
              onChange={e => setDetail(e.target.value)}
              maxLength={1000}
              rows={3}
              className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </div>

          <Button onClick={() => setStep('offers')}>Continue</Button>
        </div>
      )}

      {step === 'offers' && (
        <div className="space-y-4">
          {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

          {options.trialExtension && (
            <OfferCard
              icon={<CalendarPlus size={20} className="text-indigo-600" />}
              title={`Extend your trial by ${options.trialExtension.days} days`}
              text={`Take more time to try CertSlate. Your trial would run until ${fmtDate(options.trialExtension.newTrialEndsAt)}, with nothing charged before then. Available once.`}
              action={<Button onClick={() => extendMutation.mutate()} disabled={busy}>{extendMutation.isPending ? 'Extending…' : 'Extend my trial'}</Button>}
            />
          )}

          {options.discount && (
            <OfferCard
              icon={<Percent size={20} className="text-indigo-600" />}
              title={`${options.discount.percentOff}% off for the next ${options.discount.months} months`}
              text="Stay on your current plan at half price for your next two monthly payments, including any extra trainer seats. Then your normal price continues. Available once."
              action={<Button onClick={() => discountMutation.mutate()} disabled={busy}>{discountMutation.isPending ? 'Applying…' : 'Apply the discount'}</Button>}
            />
          )}

          <OfferCard
            icon={<Headset size={20} className="text-indigo-600" />}
            title="Talk to us first"
            text="Not getting what you need from CertSlate? We'll arrange a short call or demo to make sure you're making the most of it - no obligation."
            action={callSent
              ? <p className="text-sm font-medium text-green-700">Thanks - we'll be in touch to arrange it.</p>
              : !callOpen && <Button variant="outline" onClick={() => setCallOpen(true)}>Request a call or demo</Button>}
          >
            {callOpen && (
              <form
                onSubmit={e => { e.preventDefault(); callMutation.mutate() }}
                className="mt-4 space-y-3"
              >
                <div className="space-y-1.5">
                  <Label htmlFor="callPhone">Phone number <span className="font-normal text-gray-400">(optional - we'll use your email otherwise)</span></Label>
                  <Input id="callPhone" type="tel" value={phone} onChange={e => setPhone(e.target.value)} maxLength={30} placeholder="+44 20 7946 0000" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="callTimes">Best times <span className="font-normal text-gray-400">(optional)</span></Label>
                  <Input id="callTimes" value={times} onChange={e => setTimes(e.target.value)} maxLength={200} placeholder="Weekday mornings" />
                </div>
                {callMutation.isError && <p className="text-sm text-red-600">Sorry, that didn't send. Please try again.</p>}
                <div className="flex gap-2">
                  <Button type="submit" disabled={callMutation.isPending}>{callMutation.isPending ? 'Sending…' : 'Send request'}</Button>
                  <Button type="button" variant="ghost" onClick={() => setCallOpen(false)}>Cancel</Button>
                </div>
              </form>
            )}
          </OfferCard>

          <div className="rounded-xl border border-gray-200 bg-white p-6 space-y-3">
            <p className="font-semibold text-gray-900">Still want to cancel?</p>
            <p className="text-sm text-slate-600">
              {options.isTrialing
                ? `Your trial will end${accessEnds ? ` on ${accessEnds}` : ''} and you won't be charged.`
                : options.isAnnual
                  ? `You've paid for the year, so you'll keep full access${accessEnds ? ` until ${accessEnds}` : ' until the end of your paid year'}. We can't refund the remaining months, and you won't be charged again.`
                  : `You'll keep full access${accessEnds ? ` until ${accessEnds}` : ' until the end of your current period'}, and won't be charged again.`}
              {' '}You can change your mind before then.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                className="border-red-200 text-red-700 hover:bg-red-50"
                onClick={() => cancelMutation.mutate()}
                disabled={busy}
              >
                {cancelMutation.isPending ? 'Cancelling…' : 'Cancel my subscription'}
              </Button>
              <Button variant="ghost" onClick={() => setStep('reason')} disabled={busy}>Back</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function OfferCard({ icon, title, text, action, children }: {
  icon: React.ReactNode
  title: string
  text: string
  action?: React.ReactNode
  children?: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex gap-3">
        <div className="mt-0.5">{icon}</div>
        <div className="space-y-2 flex-1">
          <p className="font-semibold text-gray-900">{title}</p>
          <p className="text-sm text-slate-600">{text}</p>
          {action && <div className="pt-1">{action}</div>}
          {children}
        </div>
      </div>
    </div>
  )
}
